import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { FREE_TRIAL_ACTIVE_DAYS } from '@/common/constants/account';
import { useAppSelector } from '@/hooks/store';
import { useDemoMode } from '@/hooks/useDemoMode';
import { selectCurrentAccount } from '@/store/features/account';
import { formatDate } from '@/utils/date/date.helper';
import moment from 'moment';
import { InputSwitch } from 'primereact/inputswitch';
import { Tooltip } from 'primereact/tooltip';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--small-padding);
  background-color: var(--purple-middle);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--small-border-radius);
  padding: var(--medium-padding);
  margin-inline: var(--medium-padding);
  width: calc(100% - 2 * var(--medium-padding));
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: var(--semi-xsmall-padding);
  color: var(--white-main);
  font-size: var(--small-font-size);
  font-weight: 600;

  i {
    font-size: var(--medium-font-size);
  }
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--semi-xsmall-padding);
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: var(--default-border-radius);
  background-color: rgba(255, 255, 255, 0.1);
`;

const ProgressFill = styled.div<{ $percent: number }>`
  height: 100%;
  border-radius: var(--default-border-radius);
  background-color: var(--red-dark);
  width: ${({ $percent }) => $percent}%;
  transition: width 0.3s ease;
`;

const ExpiryText = styled.span`
  color: var(--gray-dark);
  font-size: var(--xsmall-font-size);
  cursor: default;
`;

const UpgradeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--xsmall-padding);
  width: 100%;
  padding: 10px var(--default-padding);
  border: none;
  border-radius: var(--default-border-radius);
  background-color: var(--red-dark);
  color: var(--white-main);
  font-family: var(--font-family);
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--red-dark-hover);
  }

  i {
    font-size: var(--xsmall-font-size);
  }
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.1);
`;

const DemoDataRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--white-main);
  font-size: var(--small-font-size);
  font-weight: 500;
`;

const DemoLabel = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xxsmall-padding);

  i {
    font-size: var(--xxsmall-font-size);
    color: var(--gray-dark);
    cursor: pointer;
  }
`;

export const FreeTrialBox: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const account = useAppSelector(selectCurrentAccount);
  const { isActive: isDemoActive, handleToggle } = useDemoMode();

  if (!account?.freeTrialEndsAt) {
    return null;
  }

  const daysRemaining = Math.max(
    0,
    moment(account.freeTrialEndsAt)
      .utc()
      .startOf('day')
      .diff(moment().utc().startOf('day'), 'days'),
  );

  const elapsedPercent = Math.max(
    0,
    Math.min(100 - (daysRemaining / FREE_TRIAL_ACTIVE_DAYS) * 100, 100),
  );

  const expiryDateFormatted =
    formatDate(account.freeTrialEndsAt, 'LL') ?? undefined;

  return (
    <Container>
      <Header>
        <i className="pi pi-clock" />
        <span>{t('account.freeTrial')}</span>
      </Header>

      <ProgressSection>
        <ProgressTrack>
          <ProgressFill $percent={elapsedPercent} />
        </ProgressTrack>
        <ExpiryText
          className="free-trial-expiry-text"
          data-pr-tooltip={expiryDateFormatted}
          data-pr-position="bottom"
        >
          {daysRemaining > 0
            ? t('freeTrial.expiresIn', { count: daysRemaining })
            : t('freeTrial.expiresToday')}
        </ExpiryText>
        <Tooltip target=".free-trial-expiry-text" />
      </ProgressSection>

      <UpgradeButton
        onClick={() =>
          navigate(RedirectPaths[RedirectPathsEnum.HUBSPOT_BOOKING](), {
            state: { from: location.pathname },
          })
        }
      >
        <span>{t('sidebar.upgrade')}</span>
        <i className="pi pi-arrow-right" />
      </UpgradeButton>

      <Separator />

      <DemoDataRow>
        <DemoLabel>
          <span>{t('freeTrial.demoData')}</span>
          <i
            className="pi pi-info-circle free-trial-demo-info"
            data-pr-tooltip={t('demoMode.off.description')}
            data-pr-position="bottom"
          />
          <Tooltip target=".free-trial-demo-info" />
        </DemoLabel>
        <InputSwitch
          className="v2"
          checked={isDemoActive}
          onChange={(e) => handleToggle(e.value)}
        />
      </DemoDataRow>
    </Container>
  );
};
